import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, DatePicker, Radio, Select, Drawer, Space } from 'antd';
import {
  ClockCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
  TableOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReportService from '../../../services/ReportService';
import { getUpdatedMobileDateRange } from '../../../helpers/mobileDateRange';

const { RangePicker } = DatePicker;
const { Option } = Select;

const InstantReports = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilterType, setDateFilterType] = useState('quick');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateFilterType, setDraftDateFilterType] = useState('quick');
  const [draftDateFilter, setDraftDateFilter] = useState('today');
  const [draftSelectedDate, setDraftSelectedDate] = useState(dayjs());
  const [draftDateRange, setDraftDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);

  const [musteriSayisi, setMusteriSayisi] = useState([]);
  const [acikMasaSayisi, setAcikMasaSayisi] = useState([]);

  const [musteriSayisiLoading, setMusteriSayisiLoading] = useState(false);
  const [acikMasaSayisiLoading, setAcikMasaSayisiLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token") || localStorage.getItem("x-access-token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const companyCode = userDetails?.CompanyCode || null;

  useEffect(() => {
    const loadBranches = async () => {
      if (!companyCode) {
        setBranches([]);
        return;
      }
      try {
        const response = await ReportService.company.getBranches({
          token,
          CompanyCode: companyCode
        });
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
          setBranches(data);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setBranches([]);
      }
    };
    loadBranches();
  }, [token, companyCode]);

  const getDateParams = (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null) => {
    const currentFilter = filterOverride !== null ? filterOverride : dateFilter;
    const currentFilterType = filterTypeOverride !== null ? filterTypeOverride : dateFilterType;
    const currentDateRange = dateRangeOverride !== null ? dateRangeOverride : dateRange;
    const currentSelectedDate = selectedDateOverride !== null ? selectedDateOverride : selectedDate;

    if (currentFilterType === 'range' && currentDateRange && currentDateRange[0] && currentDateRange[1]) {
      return {
        StartDate: currentDateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentDateRange[1].add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    if (currentFilterType === 'quick' && currentFilter === 'custom') {
      return {
        StartDate: currentSelectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentSelectedDate.add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    return {
      DateFilter: currentFilter
    };
  };

  const fetchMusteriSayisi = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null) => {
    if (!companyCode || companyCode.trim() === '') {
      setMusteriSayisi([]);
      return;
    }
    try {
      setMusteriSayisiLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.instant.getBranchCustomerCountReport({
        token,
        CompanyCode: companyCode,
        BranchCode: branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || undefined),
        ...dateParams
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setMusteriSayisi(data);
      }
    } catch (error) {
      setMusteriSayisi([]);
    } finally {
      setMusteriSayisiLoading(false);
    }
  };

  const fetchAcikMasaSayisi = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null) => {
    if (!companyCode || companyCode.trim() === '') {
      setAcikMasaSayisi([]);
      return;
    }
    try {
      setAcikMasaSayisiLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.instant.getBranchOpenTableCountReport({
        token,
        CompanyCode: companyCode,
        BranchCode: branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || undefined),
        ...dateParams
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setAcikMasaSayisi(data);
      }
    } catch (error) {
      console.error('Error fetching açık masa sayısı:', error);
      setAcikMasaSayisi([]);
    } finally {
      setAcikMasaSayisiLoading(false);
    }
  };

  const fetchAllReports = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null) => {
    setLoading(true);
    await Promise.all([
      fetchMusteriSayisi(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride, branchCodeOverride),
      fetchAcikMasaSayisi(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride, branchCodeOverride)
    ]);
    setLoading(false);
  };

  const handleBranchChange = (value) => {
    setSelectedBranch(value);
    fetchAllReports(dateFilter, dateFilterType, dateRange, selectedDate, value);
  };

  const openFilters = () => {
    setDraftDateFilterType(dateFilterType);
    setDraftDateFilter(dateFilter);
    setDraftSelectedDate(selectedDate);
    setDraftDateRange(dateRange);
    setDraftSelectedBranch(selectedBranch);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setDateFilterType(draftDateFilterType);
    setDateFilter(draftDateFilter);
    setSelectedDate(draftSelectedDate);
    setDateRange(draftDateRange);
    setSelectedBranch(draftSelectedBranch);
    setIsFilterOpen(false);

    if (draftDateFilterType === 'range') {
      fetchAllReports(null, 'range', draftDateRange, null, draftSelectedBranch);
    } else if (draftDateFilter === 'custom') {
      fetchAllReports('custom', 'quick', null, draftSelectedDate, draftSelectedBranch);
    } else {
      fetchAllReports(draftDateFilter, 'quick', null, null, draftSelectedBranch);
    }
  };

  const clearFilters = () => {
    const defaultDateFilterType = 'quick';
    const defaultDateFilter = 'today';
    const defaultSelectedDate = dayjs();
    const defaultDateRange = [dayjs().subtract(7, 'day'), dayjs()];
    const defaultSelectedBranch = null;

    setDraftDateFilterType(defaultDateFilterType);
    setDraftDateFilter(defaultDateFilter);
    setDraftSelectedDate(defaultSelectedDate);
    setDraftDateRange(defaultDateRange);
    setDraftSelectedBranch(defaultSelectedBranch);

    setDateFilterType(defaultDateFilterType);
    setDateFilter(defaultDateFilter);
    setSelectedDate(defaultSelectedDate);
    setDateRange(defaultDateRange);
    setSelectedBranch(defaultSelectedBranch);

    fetchAllReports(defaultDateFilter, defaultDateFilterType, null, null, null);
  };

  const handleMobileDraftRangeDateChange = (index, date) => {
    const nextRange = getUpdatedMobileDateRange(draftDateRange, index, date, 7);
    if (!nextRange) return;
    setDraftDateRange(nextRange);
  };

  useEffect(() => {
    fetchAllReports();
  }, []);

  const isToday = () => {
    if (dateFilterType === 'range') {
      return false;
    }
    if (dateFilter === 'custom') {
      const selected = selectedDate;
      const today = dayjs();
      return selected.format('YYYY-MM-DD') === today.format('YYYY-MM-DD');
    }
    return dateFilter === 'today';
  };

  const getReportTitle = (baseTitle) => {
    if (isToday()) {
      return `Anlık ${baseTitle}`;
    }
    return `Toplam ${baseTitle}`;
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR').format(value);
  };

  const calculateTotalCount = (data, key) => {
    return data.reduce((sum, row) => sum + parseFloat(row[key] || 0), 0);
  };

  const filterData = (data) => {
    if (!searchText) return data;
    return data.filter(item =>
      item.SubeAdi?.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const customerCountColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '40%' : '50%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Müşteri Sayısı</span>,
      dataIndex: 'MusteriSayisi',
      key: 'MusteriSayisi',
      width: isMobile ? '60%' : '50%',
      render: (value) => (
        <span className="text-base font-semibold">
          {formatNumber(value || 0)}
        </span>
      ),
      sorter: (a, b) => (a.MusteriSayisi || 0) - (b.MusteriSayisi || 0),
    },
  ];

  const openTableCountColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '40%' : '50%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Açık Masa Sayısı</span>,
      dataIndex: 'AcikMasaSayisi',
      key: 'AcikMasaSayisi',
      width: isMobile ? '60%' : '50%',
      render: (value) => (
        <span className="text-base font-semibold">
          {formatNumber(value || 0)}
        </span>
      ),
      sorter: (a, b) => (a.AcikMasaSayisi || 0) - (b.AcikMasaSayisi || 0),
    },
  ];

  const personelCountColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '40%' : '50%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Personel Sayısı</span>,
      dataIndex: 'PersonelSayisi',
      key: 'PersonelSayisi',
      width: isMobile ? '60%' : '50%',
      render: (value) => (
        <span className="text-base font-semibold">
          {formatNumber(value || 0)}
        </span>
      ),
      sorter: (a, b) => (a.PersonelSayisi || 0) - (b.PersonelSayisi || 0),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg">
              <ClockCircleOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Müşteri Masa Raporları
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Şube bazlı müşteri ve masa raporları
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchAllReports(dateFilter, dateFilterType, dateRange, selectedDate, selectedBranch)}
              loading={musteriSayisiLoading || acikMasaSayisiLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Şube ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      {/* Müşteri Sayısı Raporu */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <UserOutlined className="text-blue-500" />
            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
              {getReportTitle('Müşteri Sayısı')}
            </h2>
          </div>
        }
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
        styles={{ body: { padding: isMobile ? 12 : 18 } }}
      >
        <Table
          columns={customerCountColumns}
          dataSource={filterData(musteriSayisi)}
          loading={musteriSayisiLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = calculateTotalCount(pageData, 'MusteriSayisi');
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-blue-600">{formatNumber(total)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Açık Masa Sayısı Raporu */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <TableOutlined className="text-green-500" />
            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
              {getReportTitle('Açık Masa Sayısı')}
            </h2>
          </div>
        }
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white"
        styles={{ body: { padding: isMobile ? 12 : 18 } }}
      >
        <Table
          columns={openTableCountColumns}
          dataSource={filterData(acikMasaSayisi)}
          loading={acikMasaSayisiLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = calculateTotalCount(pageData, 'AcikMasaSayisi');
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-green-600">{formatNumber(total)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Filtre Tipi</span>
            <Radio.Group
              value={draftDateFilterType}
              onChange={(e) => setDraftDateFilterType(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="quick" className="w-1/2 text-center">Gün</Radio.Button>
              <Radio.Button value="range" className="w-1/2 text-center">Tarih Aralığı</Radio.Button>
            </Radio.Group>
          </div>

          {draftDateFilterType === 'quick' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Gün Seçimi</span>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Bugün', value: 'today' },
                    { label: 'Dün', value: 'yesterday' },
                    { label: 'Gün Seçin', value: 'custom' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDraftDateFilter(option.value)}
                      className={`
                        px-3 py-2 text-sm font-semibold transition-all duration-300 ease-out rounded-lg
                        ${draftDateFilter === option.value
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100 border border-gray-200'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {draftDateFilter === 'custom' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Özel Gün</span>
                  <DatePicker
                    value={draftSelectedDate}
                    onChange={(date) => setDraftSelectedDate(date)}
                    format="DD.MM.YYYY"
                    className="w-full"
                    placeholder="Gün seçin"
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">Tarih Aralığı</span>
              {isMobile ? (
                <div className="flex items-center gap-1 w-full">
                  <DatePicker
                    value={draftDateRange?.[0]}
                    onChange={(date) => handleMobileDraftRangeDateChange(0, date)}
                    format="DD.MM.YYYY"
                    placeholder="Başlangıç"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                  <span className="text-slate-400 text-xs">-</span>
                  <DatePicker
                    value={draftDateRange?.[1]}
                    onChange={(date) => handleMobileDraftRangeDateChange(1, date)}
                    format="DD.MM.YYYY"
                    placeholder="Bitiş"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              ) : (
                <RangePicker
                  value={draftDateRange}
                  onChange={(dates) => setDraftDateRange(dates || [])}
                  format="DD.MM.YYYY"
                  className="w-full"
                  placement="bottomLeft"
                  getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                />
              )}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftSelectedBranch}
              onChange={(value) => setDraftSelectedBranch(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              allowClear
            >
              {branches.map((branch) => (
                <Select.Option key={branch.SubeKodu} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default InstantReports;

